'use client';

import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { DEFAULT_LOCALE, translator, type Locale, type Message, type Translator } from '@/i18n';
import { LOCALE_SYNC_KEY, shouldReloadForLocale } from './locale-sync';

/**
 * The locale for CLIENT components (docs/I18N.md): resolved on the server per
 * request (`getLocale`, src/lib/viewer.ts) and handed down by the root layout,
 * the way the directory is. The value never changes on mount
 * (.claude/rules/ui-kit.md, "A context provided above the pages never changes
 * value on mount"); it changes only when the server renders again with another
 * setting, the refresh that follows a change on /account. The person's other
 * tabs in the same browser reload once on that change (./locale-sync.ts), so
 * no tab keeps a stale provider beside pages formatted in the new locale.
 *
 * `setting` is the person's stored choice (null: follow the browser), which
 * the account page's language section shows; every other component reads the
 * resolved `locale` through `useT()` or `useLocale()`.
 */
interface LocaleValue {
  locale: Locale;
  setting: Locale | null;
}

const LocaleContext = createContext<LocaleValue>({ locale: DEFAULT_LOCALE, setting: null });

export function LocaleProvider({
  locale,
  setting,
  children,
}: LocaleValue & { children: ReactNode }) {
  return (
    <LocaleContext.Provider value={{ locale, setting }}>
      <LocaleSync setting={setting} />
      {children}
    </LocaleContext.Provider>
  );
}

/** Mounted once, inside the provider: reloads this tab when the language
 *  section of another tab saves a setting other than the one this tab was
 *  rendered with (./locale-sync.ts). Renders nothing. */
function LocaleSync({ setting }: { setting: Locale | null }) {
  useEffect(() => {
    function onStorage(event: StorageEvent) {
      if (event.key !== LOCALE_SYNC_KEY) return;
      if (shouldReloadForLocale(event.newValue, setting)) window.location.reload();
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [setting]);
  return null;
}

/** The locale this page speaks. */
export function useLocale(): Locale {
  return useContext(LocaleContext).locale;
}

/** The person's stored setting: a locale, or null for "follow the browser". */
export function useLocaleSetting(): Locale | null {
  return useContext(LocaleContext).setting;
}

/** The client components' call form: `const t = useT(); t('area.thing.variant')`. */
export function useT(): Translator {
  return translator(useLocale());
}

/**
 * A descriptor as text, in the page's locale: for a component that renders
 * on both sides of the server boundary (or a server component with no `t` at
 * hand) and holds a `Message` a domain function returned.
 */
export function Msg({ message }: { message: Message }) {
  return useT().msg(message);
}
